import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GLOBAL } from '../../_config/config';
import { Observable, map } from 'rxjs';
import { SlabResponse, Slab, SlabDTO, ActiveSlab } from '../../interfaces/slab.interfaces';
import { BlService } from '../bl/bl.service';
import * as ExcelJS from 'exceljs';
import * as fs from 'file-saver';
/**
 * Servicio que gestiona los planchones (slabs) de los lotes, permitiendo
 * consultarlos, crearlos, actualizarlos y eliminarlos, generar cartas y layouts,
 * exportarlos a Excel y filtrarlos por estado y texto de búsqueda.
 */
@Injectable({
  providedIn: 'root'
})
export class SlabService {
  slabList: Slab[] = [];
  slabListFiltered: Slab[] = [];
  currentStatus: boolean = true;
  blackListSlabs: string [] = ['id', 'item', 'heat_number', 'thickness', 'nominal_length', 'bl', 'ship_number', 'ship_name', 'status'];
  headersToColor: string[] = [
    'slab_number',
    'pieces',
    'net_weight',
    'gross_weight',
    'thickness',
    'width',
    'nominal_length',
    'actual_length',
    'batch_id',
    'nominal_width'
  ];

  constructor(
    private http: HttpClient,
    private blSvc: BlService,
  ) { }

  /**
   * Obtiene la URL del layout (plantilla) para la carga de planchones.
   * @returns Observable con la URL del layout.
   */
  getSlabLayout(): Observable<{url: string}> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.salida.slab.layout}`;
    return this.http.get<{url: string}>(url);
  }

  /**
   * Obtiene los planchones de un lote, convirtiendo los pesos a toneladas.
   * @param batch_id - Identificador del lote.
   * @returns Observable con la lista de planchones.
   */
  getSlabList(batch_id: number): Observable<Slab[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.salida.slab.list}${batch_id}`;
    return this.http.get<Slab[]>(url).pipe(
      map((slabList: Slab[]) => {
        return slabList.map( slab => {
          return {
            ...slab,
            net_weight: Number((slab.net_weight / 1000).toFixed(3)),
            gross_weight: Number((slab.gross_weight / 1000).toFixed(3))
          };
        });
      })
    );
  }

  /**
   * Obtiene los planchones activos de un lote, marcándolos como activos y
   * convirtiendo los pesos a toneladas.
   * @param batch_id - Identificador del lote.
   * @returns Observable con la lista de planchones activos.
   */
  getActiveSlabList(batch_id: number): Observable<ActiveSlab[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.salida.slab.activeSlabs}${batch_id}`;
    return this.http.get<ActiveSlab[]>(url).pipe(
      map((slabList: Slab[]) => {
        return slabList.map( slab => {
          return {
            ...slab,
            status: true,
            net_weight: Number((slab.net_weight / 1000).toFixed(3)),
            gross_weight: Number((slab.gross_weight / 1000).toFixed(3))
          };
        });
      })
    );
  }

  /**
   * Crea una lista de planchones asociada a la operación actual.
   * @param slab_list - Lista de planchones a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createSlabList(slab_list: SlabDTO[]): Observable<SlabResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.salida.slab.createList}`;
    return this.http.post<SlabResponse>( url, {
      operation_id: this.blSvc.currentOperationId,
      slab_list
    } );
  }

  /**
   * Actualiza un planchón.
   * @param slab - Datos del planchón a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateSlab(slab: Slab): Observable<SlabResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.salida.slab.update}`;
    return this.http.put<SlabResponse>(url, slab);
  }

  /**
   * Elimina un planchón.
   * @param id - Identificador del planchón a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteSlab(id: number): Observable<SlabResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.salida.slab.delete}`;
    return this.http.delete<SlabResponse>(url, {
      headers: new HttpHeaders(),
      body: {
        id
      }
    });
  }

  /**
   * Elimina todos los planchones de un lote.
   * @param batch_id - Identificador del lote.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteSlabList(batch_id: number): Observable<SlabResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.salida.slab.deleteSlabList}${batch_id}`;
    return this.http.delete<SlabResponse>(url);
  }

  /**
   * Obtiene la carta asociada a un planchón.
   * @param id - Identificador del planchón.
   * @param first_date - Primera fecha del documento.
   * @param second_date - Segunda fecha del documento.
   * @param document_number - Número de documento.
   * @returns Observable con la URL de la carta.
   */
  getLetterSlab(id: number, first_date: string, second_date: string, document_number: string): Observable<{url: string}> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.salida.slab.downloadLetterSlab}`;
    return this.http.post<{url: string}>(url, {
      id,
      first_date,
      second_date,
      document_number
    });
  }

  /**
   * Genera y descarga un archivo Excel con los planchones del lote, aplicando
   * estilos a los encabezados y ajustando el ancho de las columnas.
   * @param ship_number - Número del barco usado para nombrar el archivo.
   */
  exportSlabList(ship_number: number) {
    const slabsToExport = this.slabList
      .sort((a, b) => a.id - b.id)
      .map(slab => ({
        item: this.checkNumber(slab.item) ? Number(slab.item) : slab.item,
        heat_number: slab.heat_number,
        slab_number: slab.slab_number,
        pieces: slab.pieces,
        grade: slab.grade,
        net_weight: slab.net_weight ? Number(slab.net_weight) * 1000 : 0,
        gross_weight: slab.gross_weight ? Number(slab.gross_weight) * 1000 : 0,
        thickness: slab.thickness,
        width: slab.width,
        nominal_length: slab.nominal_length,
        actual_length: slab.actual_length,
        bl: this.checkNumber(slab.bl) ? Number(slab.bl) : slab.bl,
        ship_number: slab.ship_number,
        ship_name: slab.ship_name,
        batch_id: slab.batch_id,
        nominal_width: slab.nominal_width,
        status: slab.status ? 'Activo' : 'Inactivo',
      }))

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Planchones');

    // Añadir las columnas
    const columns = Object.keys(slabsToExport[0]).map(key => ({
      header: key,
      key: key,
      width: key.length + 2,
    }));
    worksheet.columns = columns;

    // Añadir las filas
    slabsToExport.forEach(data => {
      worksheet.addRow(data);
    });

    // Aplicar estilos a los encabezados
    worksheet.getRow(1).eachCell((cell, colNumber) => {
      const cellValue: string = typeof cell.value === 'string' ? cell.value : '';
      cell.font = {
        bold: true,
        color: { argb: '000000' },
      };
      if (this.headersToColor.includes(cellValue)) {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'B7C5E4' },
        };
      }
    });

    // Ajustar el ancho de las columnas
    worksheet.columns.forEach((column: Partial<ExcelJS.Column>) => {  // Especificar el tipo de columna
      let maxLength = 0;
      if (column && column.eachCell) {  // Asegurarse de que 'eachCell' está definido
        column.eachCell({ includeEmpty: true }, (cell: ExcelJS.Cell) => {  // Especificar el tipo de 'cell'
          const cellValue = cell.value ? cell.value.toString() : '';
          maxLength = Math.max(maxLength, cellValue.length);
        });
        column.width = maxLength + 2;  // Asignar el ancho de la columna
      }
    });

    // Guardar el archivo
    workbook.xlsx.writeBuffer().then(buffer => {
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      fs.saveAs(blob, `Planchones_${ship_number}.xlsx`);
    });
  }

  /**
   * Calcula el ancho de cada columna a partir de la longitud de las cabeceras
   * y los valores de los datos.
   * @param element - Lista de filas de datos a evaluar.
   * @returns Arreglo con el ancho calculado por columna.
   */
  calculateWidths(element: any): number[] {
    return element.reduce((widths: number[], row: any) => {
      Object.keys(row).forEach((key, index) => {
        // Consider the header/title length
        const headerLength = key.length
        // Consider the value length
        const value =
          row[key] && typeof row[key] !== 'object' ? row[key].toString() : ''
        const valueLength = value.length
        // Calculate the max width between header and value, add 2 for padding
        widths[index] = Math.max(
          headerLength,
          valueLength
        )
      })
      return widths
    }, [])
  }

  /**
   * Filtra los planchones por su estado actual y, si aplica, por el texto de
   * búsqueda, ordenándolos por identificador.
   */
  filterSlabsByStatus() {
    if (this.blSvc.textFilter === '') {
      this.slabListFiltered = this.slabList.filter( slab => slab.status === this.currentStatus ).sort((a, b) => a.id - b.id);
    } else {
      this.slabListFiltered = this.slabList.filter( slab => slab.status === this.currentStatus ).filter(item => {
        const values = Object.entries(item);
        let match = false;
        values.forEach( value => {
          if (!this.blackListSlabs.includes(value[0])) {
            if ((value[1] !== null) && value[1].toString().toLowerCase().includes(this.blSvc.textFilter.toLowerCase())) {
              match = true;
            }
          }
        });
        return match;
      }).sort((a, b) => a.id - b.id);
    }
  }

  /**
   * Verifica si un valor representa un número válido.
   * @param value - Valor a evaluar.
   * @returns `true` si el valor es un número válido.
   */
  checkNumber(value: unknown): boolean {
    const regex = /^(?:\d+\.)?\d+$/;
    if (typeof value === 'string') {
      return regex.test(value);
    }
    if (typeof value === 'number') {
      return true;
    }
    return false;
  }

}
